/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  INTAKE_SECTIONS,
  IntakeSubmitSchema,
  type IntakeDraftInput,
  type IntakeSubmitInput,
} from '@therapy/shared-validators';
import { useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type IntakeView } from '../../lib/api/intake.js';
import { useAdminIntake, useAdminUpdateIntake } from '../../lib/queries/intake.js';

import { IntakeDocumentsPanel } from './documents-panel.js';
import { INTAKE_CARD_CLASS, INTAKE_STATUS_TONE, toIntakeDefaults } from './intake-form-utils.js';
import { IntakeSectionFields } from './intake-section.js';
import { IntakeSchedulePanel } from './schedule-panel.js';

/** Admin-editable view of one intake (no parent lock — staff edit after submit). */
function AdminIntakeForm({ view }: { readonly view: IntakeView }): JSX.Element {
  const { t } = useTranslation();
  const update = useAdminUpdateIntake(view.clientId);
  const { control, getValues } = useForm<IntakeSubmitInput>({
    resolver: zodResolver(IntakeSubmitSchema),
    defaultValues: toIntakeDefaults(view.form),
  });

  const onSave = useCallback(() => {
    update.mutate(getValues() as IntakeDraftInput);
  }, [update, getValues]);

  return (
    <div className="space-y-6">
      {INTAKE_SECTIONS.map((section, i) => (
        <section
          key={section}
          className={INTAKE_CARD_CLASS}
          aria-labelledby={`admin-intake-${section}`}
        >
          <h3
            id={`admin-intake-${section}`}
            className="mb-4 text-base font-semibold text-slate-800 dark:text-white"
          >
            {`${String(i + 1)}. ${t(`intake.section.${section}`)}`}
          </h3>
          <IntakeSectionFields control={control} section={section} />
        </section>
      ))}

      {update.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        {update.isSuccess && (
          <span className="text-sm text-emerald-600">{t('intake.changesSaved')}</span>
        )}
        <Button type="button" onClick={onSave} disabled={update.isPending}>
          {t('intake.saveChanges')}
        </Button>
      </div>
    </div>
  );
}

/** Admin → Client detail: review + edit the patient intake, with a status chip. */
export function AdminIntakePanel({ clientId }: { readonly clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const query = useAdminIntake(clientId);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">
          {t('admin.clientDetail.intake')}
        </h2>
        {query.data !== undefined && (
          <Badge
            label={t(`intake.status.${query.data.status}`)}
            tone={INTAKE_STATUS_TONE[query.data.status]}
          />
        )}
      </div>
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('admin.clientDetail.notFound')}
      >
        {query.data !== undefined && (
          <div className="space-y-6">
            <IntakeSchedulePanel view={query.data} />
            <AdminIntakeForm view={query.data} />
            <IntakeDocumentsPanel view={query.data} />
          </div>
        )}
      </QueryState>
    </div>
  );
}
